/** Room Designer hacim ayarları değişince (localStorage). URL zorlaması bu olayı beklemez. */
export const VOLUME_DEBUG_CHANGED_EVENT = "volume-debug-changed";

/** Model local OBB/AABB yeniden kurulunca. Overlay dünya kutusunu yeniler. */
export const SUGAR_MODEL_BOUNDS_CHANGED_EVENT = "sugar-model-bounds-changed";

const COLLISION_KEY = "sugartech:room-designer:debug-collision";
const OBB_KEY = "sugartech:room-designer:debug-obb";
const AABB_KEY = "sugartech:room-designer:debug-aabb";
const CORNER_ALIGN_KEY = "sugartech:room-designer:corner-line-snap";

/** Ekran pikseli. LineBasicMaterial.linewidth WebGL'de 1px kalır. */
export const VOLUME_DEBUG_LINE_WIDTH_PX = 3;

export type VolumeDebugColorRole = "parts" | "hull" | "contact" | "aabb";

const COLOR_DEFAULTS: Record<VolumeDebugColorRole, string> = {
  parts: "#00e676",
  hull: "#ff9100",
  contact: "#ff2222",
  aabb: "#3b82f6",
};

const COLOR_KEYS: Record<VolumeDebugColorRole, string> = {
  parts: "sugartech:room-designer:debug-color-parts",
  hull: "sugartech:room-designer:debug-color-hull",
  contact: "sugartech:room-designer:debug-color-contact",
  aabb: "sugartech:room-designer:debug-color-aabb",
};

function queryFlag(name: string): boolean {
  if (typeof window === "undefined") return false;
  const v = new URLSearchParams(window.location.search).get(name);
  return v === "1" || v === "true" || v === "";
}

function storedFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
}

function writeStoredFlag(key: string, enabled: boolean): void {
  try {
    localStorage.setItem(key, String(enabled));
  } catch {
    /* ignore quota / private mode */
  }
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(VOLUME_DEBUG_CHANGED_EVENT));
}

export function notifySugarModelBoundsChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(SUGAR_MODEL_BOUNDS_CHANGED_EVENT));
}

/** `?debugObb=1` veya ayarlar. Logger seviyesi artık tel çizmez. */
export function isObbDebugDrawEnabled(): boolean {
  if (queryFlag("debugObb")) return true;
  return storedFlag(OBB_KEY);
}

export function isObbDebugUrlForced(): boolean {
  return queryFlag("debugObb");
}

export function setObbDebugDrawEnabled(enabled: boolean): void {
  writeStoredFlag(OBB_KEY, enabled);
}

/** `?debugCollision=1` veya ayarlar. Hit teli `dragCollisionDebugDraw` içinde. */
export function isDragCollisionDebugEnabled(): boolean {
  if (queryFlag("debugCollision")) return true;
  return storedFlag(COLLISION_KEY);
}

export function isDragCollisionDebugUrlForced(): boolean {
  return queryFlag("debugCollision");
}

export function setDragCollisionDebugEnabled(enabled: boolean): void {
  writeStoredFlag(COLLISION_KEY, enabled);
}

/**
 * Sürüklerken köşe hizası (forward / right). Varsayılan açık.
 * `?cornerSnap=0` zorla kapatır.
 */
export function isCornerLineSnapEnabled(): boolean {
  if (typeof window === "undefined") return true;
  const v = new URLSearchParams(window.location.search).get("cornerSnap");
  if (v === "0" || v === "false") return false;
  if (v === "1" || v === "true" || v === "") return true;
  try {
    const stored = localStorage.getItem(CORNER_ALIGN_KEY);
    if (stored === null) return true;
    return stored === "true";
  } catch {
    return true;
  }
}

export function setCornerLineSnapEnabled(enabled: boolean): void {
  writeStoredFlag(CORNER_ALIGN_KEY, enabled);
}

/** `?debugAabb=1` veya ayarlar. Dünya eksenine hizalı kutu. */
export function isAabbDebugDrawEnabled(): boolean {
  if (queryFlag("debugAabb")) return true;
  return storedFlag(AABB_KEY);
}

export function isAabbDebugUrlForced(): boolean {
  return queryFlag("debugAabb");
}

export function setAabbDebugDrawEnabled(enabled: boolean): void {
  writeStoredFlag(AABB_KEY, enabled);
}

function storedColor(role: VolumeDebugColorRole): string {
  const fallback = COLOR_DEFAULTS[role];
  try {
    const value = localStorage.getItem(COLOR_KEYS[role]);
    if (value && /^#[0-9a-fA-F]{6}$/.test(value)) return value.toLowerCase();
  } catch {
    /* ignore quota / private mode */
  }
  return fallback;
}

export function volumeDebugColorHex(role: VolumeDebugColorRole): string {
  return storedColor(role);
}

export function volumeDebugColor(role: VolumeDebugColorRole): number {
  return Number.parseInt(storedColor(role).slice(1), 16);
}

export function setVolumeDebugColor(
  role: VolumeDebugColorRole,
  hex: string,
): void {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return;
  try {
    localStorage.setItem(COLOR_KEYS[role], hex.toLowerCase());
  } catch {
    /* ignore quota / private mode */
  }
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(VOLUME_DEBUG_CHANGED_EVENT));
}

